import { ApiError, createErrorContext, RequestError } from './errors'
import { type Method, type Options as RequestOptions, request } from './request'

export type Options = {
	/** Baseurl to prepend all requests with */
	baseUrl: string
	/** Additional headers */
	headers?: { [key: string]: string }
	/** If provided and returning a string, this is inserted as bearer-token */
	getToken?: (options: RequestOptions) => Awaitable<string | null>
	/** Called when a request's bearer token is rejected with 401. */
	onUnauthorized?: (token: string) => void
	/** If provided and returning a string, this is supplied as the X-Location header */
	getLocation?: () => string | null
	/** Never try more times */
	maxRetries?: number
	/**
	 * If provided allows async deciding if the request should be reattempted,
	 * for instance after getting a new token after expiration.
	 */
	shouldRetry?: (error: RequestError, attempts: number, options: RequestOptions) => boolean | Promise<boolean>
} & Omit<RequestOptions, 'url' | 'method' | 'data' | 'query'>

export type Api = {
	get: <T = void>(url: string, query?: any) => Promise<T>
	post: <T = void>(url: string, body?: any) => Promise<T>
	patch: <T = void>(url: string, body?: any) => Promise<T>
	put: <T = void>(url: string, body?: any) => Promise<T>
	delete: <T = void>(url: string, query?: Record<string, string | number>) => Promise<T>
	get rawText(): Api
	baseUrl: string
}

const handler = (
	{ baseUrl, headers, getToken, onUnauthorized, getLocation, maxRetries = 3, shouldRetry, ...otherOptions }: Options,
	method: Method,
) => {
	const methodHandler = async <TReturn = void>(url: string, data: any): Promise<TReturn> => {
		const options: RequestOptions = {
			...otherOptions,
			url: baseUrl + url,
			method,
			headers: { ...headers },
			data,
		}

		const context = createErrorContext(null, methodHandler)

		let attempts = 0
		const errorMessages = []

		while (attempts < maxRetries) {
			try {
				const token = getToken && (await getToken(options))
				const location = getLocation?.()
				if (token) {
					options.headers!.Authorization = `Bearer ${token}`
				}
				if (location) {
					options.headers!['X-Location'] = location
				}
				attempts++
				return await request<TReturn>(options).catch((error) => {
					if (error instanceof RequestError && error.request.statusCode === 401 && token) onUnauthorized?.(token)
					throw error
				})
			} catch (error) {
				// Only RequestErrors may be retried, everything else is unexpected
				if (!(error instanceof RequestError)) {
					throw error
				}
				if (attempts >= maxRetries || !shouldRetry || !(await shouldRetry(error, attempts, options))) {
					throw ApiError.fromRequestError({
						error,
						attempts,
						context,
						errorMessages,
					})
				}
				errorMessages.push(error.message)
			}
		}
		// This can only occur if maxRetries is 0 or negative
		throw new Error('API error')
	}

	return methodHandler
}

export const apiCreate = (options: Options): Api => ({
	get: handler(options, 'GET'),
	post: handler(options, 'POST'),
	patch: handler(options, 'PATCH'),
	put: handler(options, 'PUT'),
	delete: handler(options, 'DELETE'),
	baseUrl: options.baseUrl,
	get rawText() {
		return apiCreate({ ...options, rawText: true })
	},
})
